import { z } from "zod";
// Impostazioni → «Assegnazione dei Lead» (piano M6, Task 13, D16; mock-up `regole.html?sezione=assegnazione`): a chi va un Lead
// nuovo che entra senza un owner scelto. La regola dell'azienda (`organization_settings.assignment`) la legge ogni persona
// attiva e la cambia chi ha `settings.assignment`; porta anche chi può entrare nel turno e «I prossimi 6 Lead nuovi».
//
// Gli elenchi ripetono quelli del dominio (`@gioia365/domain`: `assignment.ts`), che i contratti non importano: un test del server
// li confronta voce per voce, e lo schema del database li lega al dominio.
/** I tre modi: «A turno fra le persone scelte», «Tutti a una persona», «A nessuno». */
export const ASSIGNMENT_MODES = ["round_robin", "single", "manual"];
/** «Un Lead che torna»: «Resta al suo owner…» (`keep_owner`) oppure «Si assegna come un Lead nuovo» (`as_new`). */
export const RETURNING_RULES = ["keep_owner", "as_new"];
/** Le persone del turno che la regola può nominare, al più: una squadra di Commerciali non ne ha centinaia. */
export const MAX_ASSIGNMENT_PEOPLE = 200;
/** Il peso più alto nel turno pesato (ASSEG-2): quanti Lead nuovi di fila riceve una persona. Lo stesso del dominio. */
export const MAX_ASSIGNMENT_WEIGHT = 10;
/**
 * La regola: il modo, le appartenenze del turno nell'ordine salvato (contano con `round_robin`), la persona unica (conta con
 * `single`), che cosa fare di un Lead che torna e i pesi del turno: per chi del turno ne ha uno diverso da 1, quanti Lead nuovi
 * riceve di fila (assente o vuoto: 1 per tutti, il turno semplice).
 */
export const assignmentSettingsSchema = z.object({
    mode: z.enum(ASSIGNMENT_MODES),
    people: z.array(z.string().min(1)).max(MAX_ASSIGNMENT_PEOPLE),
    single: z.string().min(1).nullable(),
    returning: z.enum(RETURNING_RULES),
    weights: z.record(z.string().min(1), z.number().int().min(1).max(MAX_ASSIGNMENT_WEIGHT)).optional(),
});
/**
 * Chi può entrare nel turno: i Commerciali (livello 1) dell'azienda non archiviati, in ordine alfabetico italiano. `active` dice
 * se oggi riceverebbe un Lead (appartenenza attiva e assegnabile): chi è sospeso si vede ma non si sceglie, e salta il turno.
 */
export const assignmentCandidateSchema = z.object({ membershipId: z.string(), displayName: z.string(), active: z.boolean() });
/** Un posto dell'anteprima: a chi andrebbe il Lead. */
export const assignmentNextSchema = z.object({ membershipId: z.string(), displayName: z.string() });
/**
 * `GET /api/settings/assignment` e la risposta di `PUT`: la regola (quella di partenza, «A nessuno», per un'azienda che non l'ha
 * mai cambiata, senza le persone che non sono più nell'elenco), la versione delle impostazioni dell'azienda (la stessa del
 * calendario: `409 version_conflict` se è cambiata), se chi chiede la può cambiare, chi può entrare nel turno, l'ultima
 * appartenenza servita (`null` se nessuno lo è stato) e «I prossimi 6 Lead nuovi» per la regola salvata.
 */
export const assignmentSettingsResponseSchema = z.object({
    settings: assignmentSettingsSchema,
    version: z.number().int(),
    canEdit: z.boolean(),
    candidates: z.array(assignmentCandidateSchema),
    lastServed: z.string().nullable(),
    /** Quanti Lead di fila ha preso l'ultimo servito: con i pesi dice da dove riprende l'anteprima. */
    servedInRow: z.number().int().min(1).optional(),
    next: z.array(assignmentNextSchema),
});
/** `PUT /api/settings/assignment`: la regola intera e la versione delle impostazioni letta. */
export const saveAssignmentSettingsBodySchema = z.strictObject({ settings: assignmentSettingsSchema, expectedVersion: z.number().int().min(0) });
//# sourceMappingURL=assignment.js.map